if (!customElements.get('zoom-pan')) {
  customElements.define(
    'zoom-pan',
    class ZoomPan extends HTMLElement {
      constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        this.shadowRoot.innerHTML = `
            <style>
              :host {
                  display: block;
                  position: relative;
                  overflow: hidden;
                  touch-action: none;
                  user-select: none;
                  width: 100vw;
                  height: 100vh;
                  margin: 0;
                  padding: 0;
              }
              .wrapper {
                display: block;
                transform-origin: top left;
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                will-change: transform;
                will-change: transform;
                backface-visibility: hidden;
                transform-style: preserve-3d;
              }
              .wrapper:active {
                cursor: grabbing;
              }
              ::slotted(img),
              ::slotted(video) {
                display: block;
                width: 100%;
                height: auto;
                max-width: none;
                max-height: none;
                pointer-events: none;
                user-select: none;
              }
              ::slotted(img:not(.active)),
              ::slotted(video:not(.active)) {
                display: none !important;
              }
            </style>
            <div class="wrapper">
              <slot></slot>
            </div>
          `;

        this.wrapper = this.shadowRoot.querySelector('.wrapper');
        this.scale = 1;
        this.posX = 0;
        this.posY = 0;
        this.lastDistance = null;
        this.startX = 0;
        this.startY = 0;
        this.isDragging = false;

        this.dragStartX = 0;
        this.dragStartY = 0;
        this.panStartX = 0;
        this.panStartY = 0;

        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.onMouseDown = this.onMouseDown.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);
        this.onWheel = this.onWheel.bind(this);
      }

      connectedCallback() {
        this.addEventListener('touchstart', this.onTouchStart, { passive: false });
        this.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.addEventListener('touchend', this.onTouchEnd);

        this.addEventListener('mousedown', this.onMouseDown);
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('mouseup', this.onMouseUp);

        this.addEventListener('wheel', this.onWheel, { passive: false });

        const slot = this.shadowRoot.querySelector('slot');
        slot.addEventListener('slotchange', () => {
          const media = slot.assignedElements()[0];
          if (!media) return;

          const fit = () => this.autoFit(media);
          if (media.tagName === 'IMG') {
            media.complete ? fit() : media.addEventListener('load', fit);
          } else if (media.tagName === 'VIDEO') {
            media.readyState >= 1 ? fit() : media.addEventListener('loadedmetadata', fit);
          }
        });
      }

      disconnectedCallback() {
        this.removeEventListener('touchstart', this.onTouchStart);
        this.removeEventListener('touchmove', this.onTouchMove);
        this.removeEventListener('touchend', this.onTouchEnd);
        this.removeEventListener('mousedown', this.onMouseDown);
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('mouseup', this.onMouseUp);
        this.removeEventListener('wheel', this.onWheel);
      }

      // eslint-disable-next-line no-unused-vars
      autoFit(media) {
        const containerWidth = this.clientWidth;
        const containerHeight = this.clientHeight;

        // Set wrapper to fill container exactly
        this.wrapper.style.width = `${containerWidth}px`;
        this.wrapper.style.height = `${containerHeight}px`;

        const slot = this.shadowRoot.querySelector('slot');
        const mediaEl = slot.assignedElements()[0];
        if (mediaEl) {
          mediaEl.style.width = '100%';
          mediaEl.style.height = '100%';
          mediaEl.style.objectFit = 'contain';

          // Ensure it's not triggering layout changes
          mediaEl.style.position = 'absolute';
          mediaEl.style.top = '0';
          mediaEl.style.left = '0';
        }

        // Base scale is 1 (no scale) since wrapper == container size
        this.baseScale = 1;
        this.scale = 1;

        // Position offsets zero (centered exactly)
        this.posX = 0;
        this.posY = 0;

        this.updateTransform();
      }

      updateTransform() {
        this.wrapper.style.transform = `translate3d(${this.posX}px, ${this.posY}px, 0) scale(${this.scale})`;
      }

      getDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      clampPosition() {
        const containerWidth = this.clientWidth;
        const containerHeight = this.clientHeight;

        const contentWidth = this.wrapper.offsetWidth * this.scale;
        const contentHeight = this.wrapper.offsetHeight * this.scale;

        const minX = Math.min(0, containerWidth - contentWidth);
        const minY = Math.min(0, containerHeight - contentHeight);

        const maxX = 0;
        const maxY = 0;

        this.posX = Math.min(maxX, Math.max(this.posX, minX));
        this.posY = Math.min(maxY, Math.max(this.posY, minY));
      }

      // Touch
      onTouchStart(e) {
        this.wrapper.style.transition = 'none';
        if (e.touches.length === 1) {
          this.isDragging = true;
          this.dragStartX = e.touches[0].clientX;
          this.dragStartY = e.touches[0].clientY;
          this.panStartX = this.posX;
          this.panStartY = this.posY;
        } else if (e.touches.length === 2) {
          this.lastDistance = this.getDistance(e.touches);
        }
      }

      onTouchMove(e) {
        if (e.touches.length === 2) {
          e.preventDefault();

          const newDistance = this.getDistance(e.touches);
          if (!this.lastDistance) {
            this.lastDistance = newDistance;
            return;
          }

          const delta = newDistance - this.lastDistance;
          const scaleFactor = delta * 0.005;

          let newScale = this.scale + scaleFactor;
          newScale = Math.max(this.baseScale, Math.min(newScale, 5));

          const rect = this.getBoundingClientRect();

          // Calculate pinch center (relative to container)
          const pinchX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
          const pinchY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;

          // Adjust position to zoom from pinch center
          this.posX -= ((pinchX - this.posX) * (newScale - this.scale)) / newScale;
          this.posY -= ((pinchY - this.posY) * (newScale - this.scale)) / newScale;

          this.scale = newScale;
          this.clampPosition();
          this.updateTransform();

          this.lastDistance = newDistance;
        } else if (e.touches.length === 1 && this.isDragging) {
          e.preventDefault();

          const deltaX = e.touches[0].clientX - this.dragStartX;
          const deltaY = e.touches[0].clientY - this.dragStartY;

          this.posX = this.panStartX + deltaX;
          this.posY = this.panStartY + deltaY;

          this.clampPosition();
          this.updateTransform();
        }
      }

      onTouchEnd(e) {
        this.wrapper.style.transition = 'transform 0.15s ease-out';

        if (e.touches.length < 2) {
          this.lastDistance = null;
        }

        if (e.touches.length === 1) {
          // Reset drag start to avoid jump on single finger drag after pinch
          this.dragStartX = e.touches[0].clientX;
          this.dragStartY = e.touches[0].clientY;
          this.panStartX = this.posX;
          this.panStartY = this.posY;
          this.isDragging = true;
        }

        if (e.touches.length === 0) {
          this.isDragging = false;
        }
      }

      // Mouse
      onMouseDown(e) {
        e.preventDefault();
        this.wrapper.style.transition = 'none';
        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
        this.panStartX = this.posX;
        this.panStartY = this.posY;
      }

      onMouseMove(e) {
        if (!this.isDragging) return;

        const deltaX = e.clientX - this.dragStartX;
        const deltaY = e.clientY - this.dragStartY;

        this.posX = this.panStartX + deltaX;
        this.posY = this.panStartY + deltaY;

        this.clampPosition();
        this.updateTransform();
      }

      onMouseUp() {
        this.wrapper.style.transition = 'transform 0.15s ease-out';
        this.isDragging = false;
      }

      // Wheel Zoom
      onWheel(e) {
        e.preventDefault();

        const rect = this.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        const delta = -e.deltaY * 0.0015;
        let newScale = this.scale + delta;

        // Clamp scale between baseScale and max (e.g., 5)
        newScale = Math.max(this.baseScale, Math.min(newScale, 5));

        // Adjust position to zoom centered on pointer
        this.posX -= ((offsetX - this.posX) * (newScale - this.scale)) / newScale;
        this.posY -= ((offsetY - this.posY) * (newScale - this.scale)) / newScale;

        this.scale = newScale;

        this.clampPosition();
        this.updateTransform();
      }
    },
  );
}
